// frontend/src/modules/invoicing/pages/ServicesPage.tsx
import * as React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Package, ArrowUpRight } from 'lucide-react';
import { PageHeader } from '../../../design-system/components/PageHeader';
import { Button } from '../../../components/ui/button';
import { useApi } from '../../../design-system/useApi';
import { useBranding } from '../../../core/branding/BrandingProvider';
import { formatMoney } from '../../../design-system/format';
import { VISUAL, statusPill, statusDot } from '../../../design-system/visual';
import type { Service } from '../types';

export function ServicesPage() {
  const navigate = useNavigate();
  const { tenant } = useBranding();
  const currency = (tenant as any)?.currency ?? 'USD';
  // Always fetch INCLUDING inactive so the Archived KPI is real; the toggle
  // below only controls which rows are displayed.
  const { data, loading } = useApi<{ services: Service[] }>('/api/invoicing/services?include_inactive=true');
  const [showArchived, setShowArchived] = React.useState(false);
  const services = data?.services ?? [];
  const active = services.filter((s) => s.is_active).length;
  const rows = showArchived ? services : services.filter((s) => s.is_active);

  return (
    <div>
      <PageHeader
        eyebrow="Money / Services"
        title="Services"
        description="Reusable line-item templates. Pick one when composing an invoice and we'll prefill price + tax."
        actions={
          <Link to="/dashboard/services/new">
            <Button data-tour="services.new" className="rounded-full h-10 px-4 text-[13px] font-semibold gap-1.5">
              <Plus className="h-3.5 w-3.5" />
              New service
            </Button>
          </Link>
        }
      />
      <div className="px-6 sm:px-8 lg:px-10 pb-12 space-y-6">
        <section className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          <Kpi label="Total" value={services.length} caption="In catalog" />
          <Kpi label="Active" value={active} caption="Available to invoice" tone="saffron" />
          <Kpi label="Archived" value={services.length - active} caption="Hidden" tone="muted" />
        </section>

        <section>
          <label className="inline-flex items-center gap-2 cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="h-4 w-4 rounded border-border accent-saffron"
            />
            <span className="text-[12.5px] font-semibold text-muted-foreground">Show archived</span>
          </label>
        </section>

        <section className={[VISUAL.bento, 'overflow-hidden'].join(' ')}>
          {loading ? (
            <RowSkeleton />
          ) : services.length === 0 ? (
            <EmptyServices />
          ) : rows.length === 0 ? (
            <div className="py-14 px-8 text-center">
              <p className="text-[14px] font-semibold text-foreground">Everything here is archived</p>
              <p className="text-[13px] text-muted-foreground mt-1">Tick "Show archived" above to see and restore them.</p>
            </div>
          ) : (
            <ul className="divide-y divide-border/40">
              {rows.map((s, i) => (
                <motion.li
                  key={s.id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.02, 0.18) }}
                >
                  <button
                    type="button"
                    onClick={() => navigate(`/dashboard/services/${s.id}/edit`)}
                    className="w-full text-left flex items-center gap-4 px-5 sm:px-6 py-4 hover:bg-muted/40 transition-colors"
                  >
                    <div className="h-11 w-11 rounded-2xl bg-saffron/15 ring-1 ring-saffron/30 flex items-center justify-center shrink-0">
                      <Package className="h-4 w-4 text-saffron-foreground" strokeWidth={1.7} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-[14.5px] font-semibold tracking-[-0.005em] text-foreground truncate">{s.name}</p>
                      {s.description && (
                        <p className="text-[12px] text-muted-foreground mt-0.5 truncate">{s.description}</p>
                      )}
                    </div>
                    <div className="hidden sm:flex flex-col items-end gap-1.5">
                      <div className="flex items-center gap-2">
                        <span className="text-[14px] font-semibold tabular-nums text-foreground">
                          {formatMoney(s.default_unit_price, currency)}
                        </span>
                        <span className="text-[11.5px] text-muted-foreground">/ {s.unit}</span>
                      </div>
                      <span className="text-[11px] text-muted-foreground">{Number(s.default_tax_rate)}% tax</span>
                    </div>
                    {s.is_active
                      ? <span className={statusPill('success')}><span className={statusDot('success')} />Active</span>
                      : <span className={statusPill('muted')}><span className={statusDot('muted')} />Archived</span>}
                  </button>
                </motion.li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
}

function Kpi({ label, value, caption, tone = 'neutral' }: { label: string; value: number; caption: string; tone?: 'neutral' | 'saffron' | 'muted' }) {
  return (
    <div className={[VISUAL.bento, 'p-5'].join(' ')}>
      <div className="flex items-center justify-between mb-3">
        <p className={VISUAL.eyebrow}>{label}</p>
        <span aria-hidden className={[
          'size-2 rounded-full',
          tone === 'saffron' ? 'bg-saffron' : tone === 'muted' ? 'bg-muted-foreground/40' : 'bg-foreground/40',
        ].join(' ')} />
      </div>
      <p className="font-sans font-light text-[2.2rem] leading-none tracking-[-0.03em] tabular-nums text-foreground">{value}</p>
      <p className="text-[12px] text-muted-foreground mt-2">{caption}</p>
    </div>
  );
}

function RowSkeleton() {
  return (
    <ul className="divide-y divide-border/40">
      {Array.from({ length: 3 }).map((_, i) => (
        <li key={i} className="flex items-center gap-4 px-5 py-4">
          <div className="h-11 w-11 rounded-2xl bg-muted/60 animate-pulse" />
          <div className="flex-1 space-y-1.5">
            <div className="h-3 w-1/3 rounded bg-muted/60 animate-pulse" />
            <div className="h-2.5 w-1/2 rounded bg-muted/40 animate-pulse" />
          </div>
        </li>
      ))}
    </ul>
  );
}

function EmptyServices() {
  return (
    <div className="py-14 px-8 text-center max-w-md mx-auto">
      <div className="mx-auto mb-5 h-14 w-14 rounded-2xl bg-saffron/15 ring-1 ring-saffron/25 flex items-center justify-center">
        <Package className="h-6 w-6 text-saffron-foreground" strokeWidth={1.6} />
      </div>
      <p className="text-[18px] font-semibold tracking-[-0.01em] text-foreground">Build your catalog</p>
      <p className="text-[13.5px] text-muted-foreground mt-1.5 leading-relaxed">
        Define services like "Consulting hour" or "Setup fee" once, drop them onto invoices in one click.
      </p>
      <div className="mt-6">
        <Link to="/dashboard/services/new">
          <Button className="rounded-full h-10 px-5 font-semibold gap-1.5">
            <Plus className="h-3.5 w-3.5" /> New service <ArrowUpRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </div>
  );
}
